import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import NotificationBell from './NotificationBell';

export default function Navbar() {
  const { user, logout, isAdmin, isMerchant } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const userMenuRef = useRef(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Close the mobile menu when the viewport grows past the hamburger
  // breakpoint. The dropdown renders on menuOpen alone, so without this a
  // resize back to desktop hides the ✕ button while leaving the menu itself
  // sitting across the navbar with no way to dismiss it.
  // 960px must stay in step with the .nav-hamburger media query below.
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 960px)');
    const sync = (e) => { if (!e.matches) { setMenuOpen(false); } };
    sync(mq);
    // addListener is the deprecated form, kept for older Safari.
    mq.addEventListener ? mq.addEventListener('change', sync) : mq.addListener(sync);
    return () => {
      mq.removeEventListener ? mq.removeEventListener('change', sync) : mq.removeListener(sync);
    };
  }, []);

  useEffect(() => { setMenuOpen(false); setUserMenuOpen(false); }, [location.pathname]);

  // Dismiss the profile dropdown when the user clicks outside it or hits
  // Escape. Previously it stayed open until the avatar button was clicked
  // again, which Ananya 06-30 #3 flagged as broken UX — every other
  // dismissable panel in the app already does this.
  useEffect(() => {
    if (!userMenuOpen) return;
    const onPointerDown = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setUserMenuOpen(false);
      }
    };
    const onKey = (e) => { if (e.key === 'Escape') setUserMenuOpen(false); };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [userMenuOpen]);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const isActive = (path) => location.pathname === path;

  // Where the brand logo takes the user. Anonymous visitors land on the
  // marketing splash. Logged-in users go to their role-appropriate
  // dashboard so the logo functions as a "home" jumper, matching the
  // post-login redirect rules. Without this the logo dumped logged-in
  // users back on the marketing page they'd already left.
  const logoTarget = !user ? '/'
    : isAdmin()    ? '/admin'
    : isMerchant() ? '/merchant'
    : '/coupons';

  return (
    <nav style={{
      background: scrolled ? 'rgba(255,255,255,0.92)' : '#fff',
      backdropFilter: scrolled ? 'saturate(180%) blur(12px)' : 'none',
      borderBottom: '1px solid #E4E7F0',
      position: 'sticky', top: 0, zIndex: 100,
      boxShadow: scrolled ? '0 4px 18px rgba(15,18,37,0.06)' : 'none',
      transition: 'all 0.2s',
    }}>
      <div className="container" style={{ display: 'flex', alignItems: 'center', height: 72, gap: 28 }}>

        {/* Logo */}
        <Link to={logoTarget} style={{ display: 'flex', alignItems: 'center', textDecoration: 'none', flexShrink: 0 }}>
          <img src="/images/logo.svg" alt="PerkVora" style={{ height: 38, width: 'auto', display: 'block' }}
            onError={(e) => { e.target.style.display='none'; e.target.nextSibling.style.display='inline'; }}
          />
          <span style={{ display: 'none', fontFamily: 'Plus Jakarta Sans, Inter, sans-serif', fontWeight: 800, fontSize: 22, letterSpacing: '-0.02em', color: '#0F1225' }}>
            Perk<span className="gradient-text">Vora</span>
          </span>
        </Link>

        {/* Desktop nav links */}
        <div style={{ display: 'flex', gap: 4, flex: 1, justifyContent: 'center' }} className="nav-links">
          <NavLink to="/" active={isActive('/')}>Home</NavLink>
          <NavLink to="/coupons" active={isActive('/coupons')}>Deals</NavLink>
          <NavLink to="/merchants" active={isActive('/merchants')}>Brands</NavLink>
          <NavLink to="/how-it-works" active={isActive('/how-it-works')}>How It Works</NavLink>
          <NavLink to="/for-merchants" active={isActive('/for-merchants')}>For Business</NavLink>
          <NavLink to="/pricing" active={isActive('/pricing')}>Pricing</NavLink>
          {isAdmin()    && <NavLink to="/admin" active={isActive('/admin')}>Admin</NavLink>}
          {isMerchant() && <NavLink to="/merchant" active={isActive('/merchant')}>Portal</NavLink>}
        </div>

        {/* Right side */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginLeft: 'auto' }} className="nav-right">
          {user ? (
            <>
              <NotificationBell />
              <div ref={userMenuRef} style={{ position: 'relative' }}>
                <button
                onClick={() => setUserMenuOpen(v => !v)}
                style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'none', border: '1.5px solid #E4E7F0', borderRadius: 999, padding: '5px 14px 5px 5px', cursor: 'pointer', fontSize: 14 }}
              >
                <div style={{ width: 30, height: 30, borderRadius: '50%', background: 'var(--brand-gradient)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 13, fontWeight: 700 }}>
                  {user.name?.[0]?.toUpperCase()}
                </div>
                <span style={{ maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: 500 }}>{user.name?.split(' ')[0]}</span>
                <span style={{ fontSize: 10, color: '#8B94A8' }}>▾</span>
              </button>
              {userMenuOpen && (
                <div style={{ position: 'absolute', right: 0, top: '115%', background: '#fff', border: '1px solid #E4E7F0', borderRadius: 12, boxShadow: '0 12px 32px rgba(15,18,37,0.10)', minWidth: 220, overflow: 'hidden', zIndex: 200 }}>
                  <DropItem to="/profile">My Profile</DropItem>
                  <DropItem to="/profile/wallet">Cashback Wallet</DropItem>
                  <DropItem to="/profile/redemptions">My Redemptions</DropItem>
                  <DropItem to="/profile/tier">Loyalty Tier</DropItem>
                  <DropItem to="/profile/referrals">Refer & Earn</DropItem>
                  <DropItem to="/submit-coupon">Submit a Coupon</DropItem>
                  {isAdmin()    && <DropItem to="/admin">Admin Dashboard</DropItem>}
                  {isMerchant() && <DropItem to="/merchant">Merchant Portal</DropItem>}
                  <div style={{ borderTop: '1px solid #E4E7F0', margin: '4px 0' }} />
                  <button onClick={handleLogout} style={{ width: '100%', textAlign: 'left', padding: '10px 16px', background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, color: '#DC2626', fontWeight: 500 }}>
                    Sign Out
                  </button>
                </div>
              )}
              </div>
            </>
          ) : (
            <div style={{ display: 'flex', gap: 10 }}>
              <Link to="/login"  className="btn btn-secondary btn-sm" style={{ fontWeight: 600 }}>Sign In</Link>
              <Link to="/register" className="btn btn-primary btn-sm" style={{ fontWeight: 600 }}>Join Free</Link>
            </div>
          )}
        </div>

        {/* Mobile hamburger — `marginLeft: auto` keeps it pinned to the
            right edge when .nav-right hides at <560px. Without it the
            button sat right next to the logo (Madhan 06-29 #1). */}
        <button
          onClick={() => setMenuOpen(v => !v)}
          style={{ display: 'none', background: 'none', border: 'none', cursor: 'pointer', fontSize: 26, color: '#0F1225', padding: '6px 8px', marginLeft: 'auto', lineHeight: 1, alignItems: 'center', justifyContent: 'center' }}
          className="nav-hamburger"
          aria-label="Menu"
        >{menuOpen ? '✕' : '☰'}</button>
      </div>

      {/* Mobile menu */}
      {menuOpen && (
        <div style={{ borderTop: '1px solid #E4E7F0', padding: '16px 20px 20px', background: '#fff' }}>
          <MobileLink to="/">Home</MobileLink>
          <MobileLink to="/coupons">All Deals</MobileLink>
          <MobileLink to="/merchants">Brands</MobileLink>
          <MobileLink to="/how-it-works">How It Works</MobileLink>
          <MobileLink to="/for-merchants">For Business</MobileLink>
          <MobileLink to="/pricing">Pricing</MobileLink>
          <MobileLink to="/about">About</MobileLink>
          <MobileLink to="/contact">Contact</MobileLink>
          <MobileLink to="/submit-coupon">Submit a Coupon</MobileLink>
          {!user && (
            <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
              <Link to="/login" className="btn btn-secondary btn-full">Sign In</Link>
              <Link to="/register" className="btn btn-primary btn-full">Join Free</Link>
            </div>
          )}
          {user && (
            <button onClick={handleLogout} className="btn btn-outline btn-full" style={{ marginTop: 14 }}>Sign Out</button>
          )}
        </div>
      )}

      <style>{`
        @media (max-width: 960px){
          .nav-links{display:none!important}
          .nav-hamburger{display:inline-flex!important}
        }
        @media (max-width: 560px){
          .nav-right{display:none!important}
        }
      `}</style>
    </nav>
  );
}

function NavLink({ to, active, children }) {
  return (
    <Link to={to} style={{
      padding: '8px 14px', borderRadius: 999, fontSize: 14,
      fontWeight: active ? 600 : 500,
      color: active ? '#6B2FD9' : '#4A5568',
      background: active ? '#F3EEFF' : 'transparent',
      textDecoration: 'none',
      transition: 'all 0.15s',
    }}
      onMouseEnter={e => { if (!active) e.target.style.background = '#F7F8FC'; }}
      onMouseLeave={e => { if (!active) e.target.style.background = 'transparent'; }}
    >{children}</Link>
  );
}

function DropItem({ to, children }) {
  return (
    <Link to={to} style={{ display: 'block', padding: '11px 16px', fontSize: 14, color: '#0F1225', textDecoration: 'none', fontWeight: 500 }}
      onMouseEnter={e => e.target.style.background = '#F7F8FC'}
      onMouseLeave={e => e.target.style.background = 'transparent'}
    >{children}</Link>
  );
}

function MobileLink({ to, children }) {
  return (
    <Link to={to} style={{ display: 'block', padding: '12px 0', fontSize: 15, color: '#0F1225', textDecoration: 'none', borderBottom: '1px solid #F0F2F8', fontWeight: 500 }}>
      {children}
    </Link>
  );
}
